import { useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  getBeneficiary,
  getPayment,
  getPaymentAttempts,
  retryPayment,
} from '@/mocks/api';
import { useAsync, useMutation } from '@/hooks/useAsync';
import { resolveContext } from '@/state/session';
import { CAPABILITIES, resolveCapability } from '@/services/entitlements';
import { newIdempotencyKey } from '@/services/idempotency';
import { MobileHeader } from '@/components/mobile-shell/MobileHeader';
import { Amount, DetailList, SectionHeader, StatusBadge } from '@/components/banking';
import { CardSkeleton, ErrorState, LoadingRegion, Notice } from '@/components/feedback';
import { ConfirmSheet } from '@/components/feedback/Sheet';
import { useToast } from '@/components/feedback/Toast';
import { formatDateTime } from '@/services/formatters';
import { isMockApiError } from '@/mocks/errors';

/**
 * Payment receipt and timeline.
 *
 * TRACE — retry is a separate attempt: `retryPayment` appends a new
 * PaymentAttempt with a fresh idempotency key rather than mutating the status
 * in place, so the history of what was tried survives.
 */
export function PaymentDetailPage() {
  const { paymentId = '' } = useParams();
  const resolved = resolveContext();
  const toast = useToast();
  const [confirmRetry, setConfirmRetry] = useState(false);
  // A deliberate retry is a new intent and therefore a new key.
  const retryKey = useRef<string>(newIdempotencyKey('retry'));

  const companyId = resolved?.company.id ?? null;
  const userId = resolved?.user.id ?? null;

  const payment = useAsync(async () => {
    if (!companyId) throw new Error('no session');
    return (await getPayment(companyId, paymentId)).data;
  }, [companyId, paymentId]);

  const attempts = useAsync(async () => {
    if (!paymentId) return [];
    return (await getPaymentAttempts(paymentId)).data;
  }, [paymentId, payment.data?.status]);

  const beneficiaryId = payment.data?.beneficiaryId ?? null;
  const beneficiary = useAsync(async () => {
    if (!companyId || !beneficiaryId) return null;
    return (await getBeneficiary(companyId, beneficiaryId)).data;
  }, [companyId, beneficiaryId]);

  const retryMutation = useMutation(async () => {
    if (!companyId || !userId) throw new Error('no session');
    return (await retryPayment(companyId, userId, paymentId, retryKey.current)).data;
  });

  if (!resolved) return null;

  if (payment.status === 'loading') {
    return (
      <>
        <MobileHeader title="Payment" backTo="/mobile/payments" backLabel="Payments" />
        <LoadingRegion label="Loading payment">
          <div className="tm-stack">
            <CardSkeleton height={160} />
            <CardSkeleton height={200} />
          </div>
        </LoadingRegion>
      </>
    );
  }

  if (payment.status === 'error') {
    return (
      <>
        <MobileHeader title="Payment" backTo="/mobile/payments" backLabel="Payments" />
        <ErrorState error={payment.error} onRetry={payment.reload} title="We could not load this payment" />
      </>
    );
  }

  const p = payment.data;
  const canRetry =
    (p.status === 'failed' || p.status === 'returned') &&
    resolveCapability(resolved.entitlements, CAPABILITIES.paymentsSubmit).allowed;

  return (
    <>
      <MobileHeader title="Payment" backTo="/mobile/payments" backLabel="Payments" />

      <div className="tm-stack">
        <div className="tm-card" style={{ textAlign: 'center' }}>
          <p className="tm-eyebrow">Payment to</p>
          <h2 className="tm-page-title" style={{ marginBlockEnd: 8 }}>
            {beneficiary.data?.name ?? '—'}
          </h2>
          <Amount minorUnits={p.amount} currency={p.currency} size="hero" />
          <div style={{ marginBlockStart: 10 }}>
            <StatusBadge status={p.status} />
          </div>
        </div>

        {p.status === 'awaiting-approval' ? (
          <Notice tone="info" title="Waiting for approval">
            No money has moved. This payment needs an authorised approver to decide.
          </Notice>
        ) : null}

        {p.status === 'failed' ? (
          <Notice tone="danger" title="This payment failed">
            {p.failureReason ?? 'The receiving bank rejected the payment.'} No money left your
            account.
          </Notice>
        ) : null}

        {p.status === 'returned' ? (
          <Notice tone="warning" title="This payment was returned">
            {p.failureReason ?? 'The receiving bank returned the payment.'} The amount is back in
            your account.
          </Notice>
        ) : null}

        {p.status === 'cancelled' ? (
          <Notice tone="warning" title="This payment was rejected">
            An approver rejected this payment, so no money moved.
          </Notice>
        ) : null}

        <section aria-labelledby="pay-details" className="tm-card">
          <SectionHeader title="Details" id="pay-details" />
          <div style={{ marginBlockStart: 12 }}>
            <DetailList
              items={[
                { label: 'Reference', value: p.reference },
                ...(p.noteToBeneficiary
                  ? [{ label: 'Note to payee', value: p.noteToBeneficiary }]
                  : []),
                { label: 'Created', value: formatDateTime(p.createdAt) },
                {
                  label: 'Payee check',
                  value: p.payeeVerification ? (
                    <StatusBadge status={p.payeeVerification.result} />
                  ) : (
                    'Not checked'
                  ),
                },
                ...(p.payeeVerification?.proceededDespiteWarning
                  ? [{ label: 'Warning', value: 'Sent after the payee-check warning' }]
                  : []),
                { label: 'Payment ID', value: p.id },
              ]}
            />
          </div>
        </section>

        <section aria-labelledby="pay-timeline">
          <SectionHeader title="Attempts" id="pay-timeline" />
          <div className="tm-card">
            <ol className="tm-timeline">
              {(attempts.data ?? []).map((a) => (
                <li key={a.id} className="tm-timeline__item">
                  <div className="tm-between">
                    <strong>Attempt {a.attemptNumber}</strong>
                    <StatusBadge status={a.status} />
                  </div>
                  <p className="tm-hint" style={{ margin: '2px 0 0' }}>
                    {formatDateTime(a.startedAt)}
                    {a.providerReference ? ` · ${a.providerReference}` : ''}
                  </p>
                  {a.failureReason ? (
                    <p className="tm-muted" style={{ margin: '4px 0 0' }}>
                      {a.failureReason}
                    </p>
                  ) : null}
                </li>
              ))}
            </ol>
          </div>
        </section>

        {canRetry ? (
          <button
            type="button"
            className="tm-btn tm-btn--block"
            onClick={() => setConfirmRetry(true)}
          >
            Try this payment again
          </button>
        ) : null}
      </div>

      <ConfirmSheet
        open={confirmRetry}
        onClose={() => setConfirmRetry(false)}
        busy={retryMutation.pending}
        onConfirm={() => {
          void retryMutation.run().then((outcome) => {
            if (outcome.ok) {
              payment.setData(outcome.value.payment);
              attempts.reload();
              // A fresh key so a further retry is again a distinct attempt.
              retryKey.current = newIdempotencyKey('retry');
              toast.show({
                message:
                  outcome.value.outcome === 'completed'
                    ? 'Payment sent on the second attempt.'
                    : 'The payment failed again.',
              });
            } else if (outcome.error) {
              toast.show({
                message: isMockApiError(outcome.error)
                  ? outcome.error.message
                  : 'We could not retry this payment.',
              });
            }
            setConfirmRetry(false);
          });
        }}
        title="Try this payment again?"
        body="This creates a new attempt using the same details. The earlier attempt stays in the history."
        confirmLabel="Try again"
      />
    </>
  );
}
